import React, { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Archive, Newspaper, Users, Send, ChevronDown, Menu, X, MessageCircle, Search } from 'lucide-react';
import { MEDIA_URL } from '../constants';

const Navbar = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const [mobileOpen, setMobileOpen]       = useState(false);
  const [contributeOpen, setContributeOpen] = useState(false);
  const [searchVal, setSearchVal]         = useState('');
  const searchRef = useRef(null);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navLinks = [
    { to: '/',                label: 'Home',             icon: <Home className="w-4 h-4" /> },
    { to: '/archive',         label: 'Archive',          icon: <Archive className="w-4 h-4" /> },
    { to: '/news',            label: 'News',             icon: <Newspaper className="w-4 h-4" /> },
    { to: '/history-heritage',label: 'History & Heritage',icon: <span className="text-base">📜</span> },
    { to: '/chatroom',        label: 'Global Chatroom',  icon: <MessageCircle className="w-4 h-4" /> },
    { to: '/credits',         label: 'Admin & Credits',  icon: <Users className="w-4 h-4" /> },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchVal.trim().length < 2) return;
    navigate(`/search?q=${encodeURIComponent(searchVal.trim())}`);
    setSearchVal('');
    setMobileOpen(false);
  };

  return (
    <header className="bg-emerald-800 text-white shadow-lg sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Centered branding */}
        <div className="flex flex-col items-center mb-4">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-90 transition-opacity">
            <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center overflow-hidden shadow-md">
              <img
                src={`${MEDIA_URL}/images/gbongan_logo.png`}
                alt="Gbongan Archive Logo"
                className="w-full h-full object-cover"
                onError={e => { e.target.style.display='none'; }}
              />
            </div>
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Gbongan Archive</h1>
              <p className="text-sm italic text-emerald-200 mt-0.5 text-center">...where history meets the future...</p>
            </div>
          </Link>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="flex justify-center mb-3">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-300 pointer-events-none" />
            <input
              ref={searchRef}
              type="text"
              value={searchVal}
              onChange={e => setSearchVal(e.target.value)}
              placeholder="Search people, places, news…"
              className="w-full pl-9 pr-20 py-2 rounded-full bg-emerald-700/60 text-white placeholder-emerald-300 border border-emerald-600 focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm"
            />
            <button
              type="submit"
              disabled={searchVal.trim().length < 2}
              className="absolute right-1 top-1/2 -translate-y-1/2 px-4 py-1 rounded-full bg-amber-500 hover:bg-amber-400 text-white text-xs font-semibold transition disabled:opacity-50"
            >
              Search
            </button>
          </div>
        </form>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center justify-center gap-5 text-sm font-medium flex-wrap">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition ${
                isActive(link.to) && link.to !== '/'
                  ? 'bg-emerald-600 text-white'
                  : location.pathname === '/' && link.to === '/'
                  ? 'bg-emerald-600 text-white'
                  : 'hover:text-emerald-200 hover:bg-emerald-700/50'
              }`}
            >
              {link.icon} {link.label}
            </Link>
          ))}
          {/* Contribute dropdown */}
          <div className="relative">
            <button
              onClick={() => setContributeOpen(o => !o)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:text-emerald-200 hover:bg-emerald-700/50 transition"
            >
              <Send className="w-4 h-4" /> Submit
              <ChevronDown className={`w-3 h-3 transition-transform ${contributeOpen ? 'rotate-180' : ''}`} />
            </button>
            {contributeOpen && (
              <div className="absolute top-full left-0 mt-2 bg-white text-gray-800 rounded-lg shadow-xl py-2 w-52 z-50">
                <Link to="/contribute/archive" onClick={() => setContributeOpen(false)} className="block px-4 py-2 hover:bg-emerald-50 text-sm">📚 Archive Contribution</Link>
                <Link to="/contribute/news" onClick={() => setContributeOpen(false)} className="block px-4 py-2 hover:bg-emerald-50 text-sm">📰 News Submission</Link>
                <Link to="/contribute/person" onClick={() => setContributeOpen(false)} className="block px-4 py-2 hover:bg-emerald-50 text-sm">👤 Person Profile</Link>
                <Link to="/contribute/contact" onClick={() => setContributeOpen(false)} className="block px-4 py-2 hover:bg-emerald-50 text-sm">📬 Contact Us</Link>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile menu button */}
        <button
          className="md:hidden absolute top-4 right-4 text-white"
          onClick={() => setMobileOpen(o => !o)}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden bg-emerald-900 border-t border-emerald-700 px-4 py-4 space-y-2">
          {/* Mobile search */}
          <form onSubmit={handleSearch} className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" />
              <input
                type="text"
                value={searchVal}
                onChange={e => setSearchVal(e.target.value)}
                placeholder="Search archive…"
                className="w-full pl-9 pr-4 py-2 rounded-full bg-emerald-700 text-white placeholder-emerald-300 border border-emerald-600 focus:outline-none text-sm"
              />
            </div>
            <button type="submit" className="px-3 py-2 bg-amber-500 rounded-full text-white text-sm font-semibold">Go</button>
          </form>
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-emerald-700 text-sm"
            >
              {link.icon} {link.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-emerald-700">
            <p className="text-xs text-emerald-400 px-3 mb-2">Submit Contribution</p>
            {[['archive','📚 Archive'],['news','📰 News'],['person','👤 Person'],['contact','📬 Contact']].map(([type, label]) => (
              <Link key={type} to={`/contribute/${type}`} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-emerald-700 text-sm">{label}</Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
